import React, { FunctionComponent, ReactNode } from 'react';

import { ICapture } from 'interfaces';
import { Button, ButtonGroup, Col, Icon, IconButton, Row, Tooltip } from 'cfa-react-components';
import { Gear, Emergency } from '@cfa-icons/brand';
import { CaptureConfigService, CaptureService, UserService } from 'services';
import { TaskState, UserRole } from 'enums';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faRedoAlt, faStop, faTrash } from '@fortawesome/free-solid-svg-icons';

interface ICaptureItemProps {
    capture: ICapture,
    openRepeatModal: (c: ICapture) => void,
    openReplayModal: (c: ICapture) => void,
    openDeleteModal: (target: any, deleteFunc: (any) => Promise<any>, deleteVerb: string, deleteNoun: string) => void,
}

export const CaptureItem: FunctionComponent<ICaptureItemProps> = ({ capture, openRepeatModal, openReplayModal, openDeleteModal }) => {

    const config = CaptureConfigService.allCaptureConfigsMap[capture.configUUID];

    const configContent: ReactNode = config && (<>
        <Row>
            <h2 className='col'>Configuration Details</h2>
        </Row>
        <Row>
            <Col>{'Name:'}</Col>
            <Col>{config.name}</Col>
        </Row>
        <Row>
            <Col>{'UUID:'}</Col>
            <Col>{config.captureConfigUUID}</Col>
        </Row>
        <Row>
            <Col>{'Source Type:'}</Col>
            <Col>{config.sourceType}</Col>
        </Row>
        <Row>
            <Col>{'Source Location:'}</Col>
            <Col>{config.sourceParams.location}</Col>
        </Row>
        <Row>
            <Col>{'Source Secret Name:'}</Col>
            <Col>{config.sourceParams.secretPath}</Col>
        </Row>
        <Row>
            <Col>{'Duration to capture:'}</Col>
            <Col>{config.durationMinutes} {config.durationMinutes > 1 ? 'minutes' : 'minute'}</Col>
        </Row>
        <Row>
            <Col>{'Delay to start:'}</Col>
            <Col>{config.delayMinutes} {config.delayMinutes > 1 ? 'minutes' : 'minute'}</Col>
        </Row>
        <Row className='rowLastItem'>
            <Col>{'Message TTL:'}</Col>
            <Col>{config.TTL} {config.TTL > 1 ? 'days' : 'day'}</Col>
        </Row>
    </>);

    return (
        <tr key={capture.captureUUID}>
            <td className='itemTable_item'>
                {capture.name}<br/>
                <p style={{fontSize: '12px', margin: '6px 6px 0px'}}>{capture.captureUUID}</p>
            </td>
            <td className='itemTable_item itemTable_itemButton'>
                <Tooltip 
                    content={configContent}
                    placement='right-start'
                    showOnElementEvents={['click']}
                    style={{width: '600px'}}
                >
                    <IconButton size="sm">
                        <Icon icon={Gear} />
                    </IconButton>
                </Tooltip>
            </td>
            <td className='itemTable_item'>
                {capture.startTime > 0 ? new Date(capture.startTime*1000).toLocaleString() : capture.status}
            </td>
            <td className='itemTable_item'>
                {capture.endTime > 0 ? new Date(capture.endTime*1000).toLocaleString() : capture.status}
            </td>
            <td className='itemTable_item'>
                {capture.messageCount}
            </td>
            <td className='itemTable_item'>
                {capture.messageTTL > 0 ? new Date(capture.messageTTL*1000).toLocaleString() : capture.status}
            </td>
            <td className='itemTable_item itemTable_itemButton'> 
                <ButtonGroup size='sm'>
                    <Tooltip content="Repeat" showOnElementDelay={{showDelay: 1000}}>
                        <Button className='itemTable_iconButton' color='secondary' onClick={() => openRepeatModal(capture)} 
                            disabled={capture.status != TaskState.Success}>
                            <FontAwesomeIcon icon={faRedoAlt} />
                        </Button>
                    </Tooltip>
                    <Tooltip content="Replay" showOnElementDelay={{showDelay: 1000}}>
                        <Button className="itemTable_iconButton" onClick={() => openReplayModal(capture)} size="sm" 
                            disabled={capture.status != TaskState.Success}>
                            <FontAwesomeIcon icon={faPlay} />
                        </Button>
                    </Tooltip>
                    {(capture.status === TaskState.Running || capture.status === TaskState.Pending) ? (
                        <Tooltip content="Cancel" showOnElementDelay={{showDelay: 1000}}>
                            <Button className="itemTable_iconButton" variant="destructive"
                                onClick={() => openDeleteModal(capture, CaptureService.deleteCapture, 'Cancel', 'Capture')}>
                                <FontAwesomeIcon icon={faStop} />
                            </Button>
                        </Tooltip>
                    ) : (
                        <Tooltip content="Delete" showOnElementDelay={{showDelay: 1000}}>
                            <Button className="itemTable_iconButton" variant="destructive"
                                onClick={() => openDeleteModal(capture, CaptureService.deleteCapture, 'Delete', 'Capture')}
                                disabled={UserService.oktaUserInfo.role < UserRole.Admin}>
                                <FontAwesomeIcon icon={faTrash} />
                            </Button>
                        </Tooltip>
                    )}
                </ButtonGroup>
            </td>
        </tr>
    );
}